import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatCard, StatusChip } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Users, DollarSign, Wrench, AlertTriangle, Clock, CheckSquare, FlaskConical, CreditCard } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function CommandCenter() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [d, setD] = useState(null);
  const [cpr, setCpr] = useState([]);

  const decideCpr = async (r, approve) => {
    let price = null;
    if (approve) {
      const p = window.prompt(`Approve custom pricing for ${r.customer_name}. Enter the authorized custom price ($):`, "");
      if (p === null) return;
      price = Number(p);
      if (!price || price <= 0) { toast.error("Enter a valid price"); return; }
    }
    const reason = window.prompt(`${approve ? "Approve" : "Deny"} — Owner reason (required):`, "");
    if (!reason) { toast.error("Reason required"); return; }
    try { await api.post(`/custom-pricing-requests/${r.id}/decide`, { approve, price, reason }); toast.success(approve ? `Custom price $${price} set` : "Request denied"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const load = async () => { const { data } = await api.get("/dashboard/command-center"); setD(data); try { const c = await api.get("/custom-pricing-requests", { params: { status: "all" } }); setCpr(c.data); } catch { setCpr([]); } };
  useEffect(() => { load(); }, []);
  if (!d) return <Loading label="Loading command center" />;

  const toggleFieldTest = async () => {
    try { const { data } = await api.put("/settings/field-test", { field_test_mode: !d.field_test_mode });
      toast[data.field_test_mode ? "warning" : "success"](`Field Test Mode ${data.field_test_mode ? "ON" : "OFF"}`);
      window.dispatchEvent(new Event("field-test-changed"));
      load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const recommend = async (c) => {
    const rec = window.confirm(`Recommend APPROVE for ${c.employee_name}'s ${c.field}? (Cancel = recommend DENY)`) ? "approve" : "deny";
    const reason = window.prompt("Manager reason (required):", "");
    if (!reason) { toast.error("Reason required"); return; }
    try { await api.post(`/time/corrections/${c.id}/recommend`, { recommend: rec, reason }); toast.success("Recommendation sent to Owner"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decide = async (c, approve) => {
    const reason = window.prompt(`Owner decision reason (required) for ${approve ? "APPROVE" : "DENY"}:`, "");
    if (!reason) { toast.error("Reason required"); return; }
    try { await api.post(`/time/corrections/${c.id}/decide`, { approve, reason }); toast.success(approve ? "Correction applied" : "Correction denied"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decideReq = async (r, approve) => {
    const reason = window.prompt(`${approve ? "Approve" : "Deny"} ${r.equipment_name} → ${r.requested_status.toUpperCase()}. Note (optional):`, "");
    if (reason === null) return;
    try { await api.post(`/equipment/status-requests/${r.id}/approve`, { approve, reason }); toast.success(approve ? "Approved" : "Denied"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const TABS = ["operations", "crews", "timekeeping", "approvals", "jobs", "equipment", "memberships", "field-test", "money"];

  return (
    <div className="max-w-6xl">
      <SectionTitle sub={`Live operations · ${d.date}`} right={
        <button className="sg-btn" data-testid="cc-fieldtest-toggle" onClick={toggleFieldTest}
          style={{ borderColor: d.field_test_mode ? "#F59E0B" : "var(--brd)", color: d.field_test_mode ? "#F59E0B" : "var(--txt)" }}>
          <FlaskConical size={16} /> Field Test Mode: {d.field_test_mode ? "ON" : "OFF"}
        </button>
      }>Owner Command Center</SectionTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active Crews" value={d.active_crews} icon={Users} testid="cc-crews" />
        <StatCard label="Today's Revenue (live)" value={`$${d.today_revenue.toLocaleString()}`} accent icon={DollarSign} testid="cc-revenue" />
        <StatCard label="Jobs Completed" value={d.jobs_completed} icon={Activity} testid="cc-jobs" />
        <StatCard label="Clocked In Now" value={d.timekeeping.clocked_in_count} icon={Clock} testid="cc-clockedin" />
      </div>

      <Tabs defaultValue="operations">
        <TabsList className="flex flex-wrap gap-1 bg-transparent p-0 h-auto mb-4">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`cc-tab-${t}`}
              className="capitalize sg-btn data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white data-[state=active]:border-[var(--accent)]"
              style={{ minHeight: 44 }}>{t.replace("-", " ")}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="operations" data-testid="cc-panel-operations">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Memberships" value={d.memberships} testid="cc-mem" />
            <StatCard label="Equipment Alerts" value={d.equipment_alerts} icon={Wrench} testid="cc-equip" />
            <StatCard label="Rework Open" value={d.rework_open} icon={AlertTriangle} testid="cc-rework" />
            <StatCard label="Attainment" value={`${d.crews[0]?.attainment || 0}%`} accent testid="cc-attain" />
          </div>
        </TabsContent>

        <TabsContent value="crews" data-testid="cc-panel-crews">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {d.crew_status.map((c) => (
              <div key={c.crew_id} className="sg-card p-5" data-testid={`cc-crewstatus-${c.crew_number}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-head text-lg font-bold">{c.crew_number}</span>
                  <StatusChip status={c.ready ? "ready" : "damaged"} label={c.ready ? (c.override ? "Ready (Override)" : "Ready") : "Blocked"} />
                </div>
                <div className="text-xs mb-2" style={{ color: "var(--txt2)" }}>{c.members.map((m) => `${m.name} (${m.role})`).join(", ") || "No members"} · {c.clocked_in_count} clocked in</div>
                {c.current_job ? (
                  <div className="text-sm">Current: <b>{c.current_job.title}</b> · {c.current_job.stage} · {c.current_job.customer_name}</div>
                ) : <div className="text-sm" style={{ color: "var(--txt2)" }}>No active job</div>}
                {!c.ready && <div className="text-xs mt-2" style={{ color: "#EF4444" }}>Blocking: {c.blocking_reason}</div>}
                {c.override && <div className="text-xs mt-1" style={{ color: "#F59E0B" }}>Override active by {c.override.approved_by} — {c.override.reason}</div>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="timekeeping" data-testid="cc-panel-timekeeping">
          <div className="sg-card p-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-timekeeping-table">
              <thead><tr style={{ color: "var(--txt2)" }} className="text-left text-xs uppercase tracking-wider">
                <th className="p-2">Employee</th><th className="p-2">Clock In</th><th className="p-2">Clock Out</th><th className="p-2">Duration</th><th className="p-2">Adjustments</th></tr></thead>
              <tbody>
                {d.timekeeping.entries.slice(0, 50).map((e) => (
                  <tr key={e.id} className="border-t" style={{ borderColor: "var(--brd)" }} data-testid={`cc-time-${e.id}`}>
                    <td className="p-2">{e.user_name}</td>
                    <td className="p-2 font-mono text-xs">{fmtTime(e.clock_in)}</td>
                    <td className="p-2 font-mono text-xs">{e.clock_out ? fmtTime(e.clock_out) : <StatusChip status="ready" label="On clock" />}</td>
                    <td className="p-2">{fmtDur(e.duration_seconds)}</td>
                    <td className="p-2">
                      {(e.adjustments || []).length > 0 ? (
                        <details data-testid={`cc-time-history-${e.id}`}>
                          <summary className="cursor-pointer"><StatusChip status="warning" label={`${e.adjustments.length} corrected`} /></summary>
                          <div className="mt-1 flex flex-col gap-1">
                            {e.adjustments.map((a, i) => (
                              <div key={i} className="text-xs" style={{ color: "var(--txt2)" }}>
                                {a.field}: <span className="font-mono">{fmtTime(a.from)}</span> → <span className="font-mono" style={{ color: "var(--accent)" }}>{fmtTime(a.to)}</span>
                                {" · "}req {a.requested_by || "—"}{a.manager_name ? ` · mgr ${a.manager_name} (${a.manager_recommendation})` : ""} · owner {a.decided_by} “{a.owner_reason}”
                              </div>
                            ))}
                          </div>
                        </details>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="approvals" data-testid="cc-panel-approvals">
          <div className="sg-card p-5 mb-4">
            <div className="font-head font-bold mb-3 flex items-center gap-2"><Clock size={18} /> Time Corrections</div>
            {d.approvals.pending_time_corrections.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No pending time corrections.</div>}
            {d.approvals.pending_time_corrections.map((c) => (
              <div key={c.id} className="sg-card-elev p-3 mb-2" data-testid={`cc-correction-${c.id}`}>
                <div className="text-sm"><b>{c.employee_name}</b> · {c.field}: <span className="font-mono">{fmtTime(c.original_value)}</span> → <span className="font-mono" style={{ color: "var(--accent)" }}>{fmtTime(c.requested_value)}</span></div>
                <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Requested by {c.requested_by_name} ({c.requested_by_role}) · Reason: {c.reason}</div>
                {c.status === "pending_owner" && <div className="text-xs mt-1" style={{ color: "#F59E0B" }}>Manager recommends {c.manager_recommendation?.toUpperCase()} — {c.manager_reason}</div>}
                <div className="flex gap-2 mt-2">
                  {c.status === "pending_manager" && (user.role === "manager" || isOwner) && <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => recommend(c)} data-testid={`cc-recommend-${c.id}`}>Recommend</button>}
                  {c.status === "pending_owner" && isOwner && <>
                    <button className="sg-btn sg-btn-accent" style={{ minHeight: 36 }} onClick={() => decide(c, true)} data-testid={`cc-decide-approve-${c.id}`}>Approve</button>
                    <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => decide(c, false)} data-testid={`cc-decide-deny-${c.id}`}>Deny</button>
                  </>}
                  {c.status === "pending_owner" && !isOwner && <span className="text-xs" style={{ color: "var(--txt2)" }}>Awaiting Owner decision</span>}
                  {c.status === "pending_manager" && user.role === "technician" && <span className="text-xs" style={{ color: "var(--txt2)" }}>Awaiting Manager review</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="sg-card p-5 mb-4" data-testid="cc-custom-pricing">
            <div className="font-head font-bold mb-3 flex items-center gap-2"><DollarSign size={18} /> Custom Pricing Requests</div>
            {cpr.filter((r) => r.status === "pending").length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No pending custom pricing requests.</div>}
            {cpr.filter((r) => r.status === "pending").map((r) => (
              <div key={r.id} className="sg-card-elev p-3 mb-2" data-testid={`cc-cpr-${r.id}`}>
                <div className="text-sm"><b>{r.customer_name}</b> · {r.address}</div>
                <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>By {r.requested_by_name} ({r.requested_by_role}) · {r.reason}{r.scope ? ` · scope: ${r.scope}` : ""}</div>
                {isOwner ? (
                  <div className="flex gap-2 mt-2">
                    <button className="sg-btn sg-btn-accent" style={{ minHeight: 36 }} onClick={() => decideCpr(r, true)} data-testid={`cc-cpr-approve-${r.id}`}>Set Price</button>
                    <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => decideCpr(r, false)} data-testid={`cc-cpr-deny-${r.id}`}>Deny</button>
                  </div>
                ) : <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Awaiting Owner pricing decision</div>}
              </div>
            ))}
            {cpr.filter((r) => r.status !== "pending").slice(0, 5).map((r) => (
              <div key={r.id} className="text-xs mt-1" style={{ color: "var(--txt2)" }} data-testid={`cc-cpr-history-${r.id}`}>
                <StatusChip status={r.status === "priced" ? "ready" : "warning"} label={r.status} /> {r.customer_name} {r.owner_price ? `· $${r.owner_price}` : ""} · {r.decided_by}
              </div>
            ))}
          </div>
          <div className="sg-card p-5">
            <div className="font-head font-bold mb-3 flex items-center gap-2"><CheckSquare size={18} /> Equipment Status Requests</div>
            {d.approvals.pending_status_requests.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No pending equipment requests.</div>}
            {d.approvals.pending_status_requests.map((r) => (
              <div key={r.id} className="sg-card-elev p-3 mb-2" data-testid={`cc-req-${r.id}`}>
                <div className="text-sm"><b>{r.equipment_name}</b> · {r.current_status} → <span style={{ color: "var(--accent)" }}>{(r.requested_status || "").toUpperCase()}</span></div>
                <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>By {r.requested_by_name} · {r.reason}</div>
                <div className="flex gap-2 mt-2">
                  <button className="sg-btn sg-btn-accent" style={{ minHeight: 36 }} onClick={() => decideReq(r, true)} data-testid={`cc-req-approve-${r.id}`}>Approve</button>
                  <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => decideReq(r, false)} data-testid={`cc-req-deny-${r.id}`}>Deny</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="jobs" data-testid="cc-panel-jobs">
          <div className="flex flex-col gap-2" data-testid="cc-jobops">
            {d.job_ops.map((j) => (
              <div key={j.id} className="sg-card p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`cc-jobop-${j.id}`}>
                <div className="text-sm">
                  <b>{j.title || "Untitled"}</b> · {j.customer_name || "—"} · {j.crew_number}
                  <div className="text-xs" style={{ color: "var(--txt2)" }}>{j.address}</div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {j.is_test && <span className="chip" style={{ background: "rgba(245,158,11,0.18)", color: "#F59E0B", borderColor: "#F59E0B", fontWeight: 700 }}>TEST</span>}
                  <StatusChip status={j.stage === "closed" ? "ready" : "in_progress"} label={j.stage} />
                  <StatusChip status={j.payment_state} />
                  {j.qc_complete ? <StatusChip status="ready" label="QC ✓" /> : <StatusChip status="warning" label="QC pending" />}
                  <span className="font-mono text-xs" style={{ color: "var(--txt2)" }}>${(j.collected_revenue || 0).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="equipment" data-testid="cc-panel-equipment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {d.equipment_detail.map((e) => (
              <div key={e.id} className="sg-card p-3 flex items-center justify-between" data-testid={`cc-equip-${e.id}`}>
                <div className="text-sm">{e.name} {e.critical && <span className="text-xs" style={{ color: "var(--accent)" }}>· critical</span>}</div>
                <StatusChip status={e.status} />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="field-test" data-testid="cc-panel-field-test">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <StatCard label="Field Test Jobs" value={d.field_test.test_jobs} icon={FlaskConical} testid="cc-ft-jobs" />
            <StatCard label="Test Collected (isolated)" value={`$${d.field_test.test_collected.toLocaleString()}`} testid="cc-ft-collected" />
            <StatCard label="Field Test Mode" value={d.field_test_mode ? "ON" : "OFF"} accent={d.field_test_mode} testid="cc-ft-mode" />
          </div>
          <div className="sg-card p-4 text-sm" style={{ color: "var(--txt2)" }}>Controlled Field Test data is isolated from all live revenue, production and financial KPIs. Jobs run the full real workflow so field testing stays meaningful.</div>
        </TabsContent>

        <TabsContent value="memberships" data-testid="cc-panel-memberships">
          <MembershipsTab />
        </TabsContent>

        <TabsContent value="money" data-testid="cc-panel-money">
          {isOwner ? <MoneyTab /> : <div className="sg-card p-6 text-sm" style={{ color: "var(--txt2)" }}>Financial detail is owner-only.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MembershipsTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/memberships/dashboard").then(({ data }) => setD(data)).catch(() => setD({ memberships: [], failed: [], active_count: 0, mrr: 0, failed_count: 0 })); }, []);
  if (!d) return <Loading label="Loading memberships" />;
  const statusTone = (s) => (s === "ACTIVE" ? "#10B981" : s === "PENDING" ? "#F59E0B" : "#EF4444");
  return (
    <div data-testid="memberships-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard label="Active Memberships" value={d.active_count} icon={CreditCard} testid="mem-active" />
        <StatCard label="Monthly Recurring (Sandbox)" value={`$${Number(d.mrr).toLocaleString()}`} accent icon={DollarSign} testid="mem-mrr" />
        <StatCard label="Failed Payments" value={d.failed_count} icon={AlertTriangle} testid="mem-failed" />
        <StatCard label="Total On File" value={d.total} testid="mem-total" />
      </div>

      {d.failed_count > 0 && (
        <div className="sg-card p-4 mb-4" style={{ borderColor: "#EF4444" }} data-testid="mem-failed-panel">
          <div className="font-head font-bold mb-2" style={{ color: "#EF4444" }}>Payment method needs attention</div>
          {d.failed.map((m) => (
            <div key={m.id} className="text-sm py-1" style={{ color: "var(--txt2)" }} data-testid={`mem-failed-${m.id}`}>
              <b style={{ color: "var(--txt)" }}>{m.customer_name}</b> — {m.tier_label} (${m.monthly}/mo) · last payment FAILED · {m.card_brand} ••••{m.card_last4}
            </div>
          ))}
        </div>
      )}

      <div className="sg-card p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="memberships-table">
          <thead><tr style={{ color: "var(--txt2)" }} className="text-left">
            <th className="p-3">Customer</th><th className="p-3">Tier</th><th className="p-3">Status</th>
            <th className="p-3">Next / Paid Through</th><th className="p-3">Benefit End</th><th className="p-3">$/mo</th>
          </tr></thead>
          <tbody>
            {d.memberships.length === 0 && <tr><td className="p-3" colSpan={6} style={{ color: "var(--muted)" }}>No membership subscriptions yet.</td></tr>}
            {d.memberships.map((m) => (
              <tr key={m.id} style={{ borderTop: "1px solid var(--brd)" }} data-testid={`mem-row-${m.id}`}>
                <td className="p-3"><div style={{ color: "var(--txt)" }}>{m.customer_name}</div><div className="text-xs" style={{ color: "var(--muted)" }}>{m.customer_email}</div></td>
                <td className="p-3">{m.tier_label}</td>
                <td className="p-3">
                  {m.benefit_expired ? (
                    <span style={{ color: "#EF4444", fontWeight: 700 }}>EXPIRED</span>
                  ) : m.cancel_scheduled ? (
                    <span className="chip" style={{ fontSize: 11, color: "#F59E0B", borderColor: "#F59E0B" }}>Cancel scheduled · benefit through {m.benefit_end_date || "period end"}</span>
                  ) : (
                    <span style={{ color: statusTone(m.status), fontWeight: 700 }}>{m.status}</span>
                  )}
                  {m.last_payment_status === "FAILED" && <span className="text-xs block mt-1" style={{ color: "#EF4444" }}>payment failed</span>}
                </td>
                <td className="p-3" style={{ color: "var(--txt2)" }}>{m.next_billing_date || "—"}</td>
                <td className="p-3" style={{ color: "var(--txt2)" }}>{m.benefit_end_date || "—"}</td>
                <td className="p-3 font-mono">${m.monthly}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }} data-testid="mem-sandbox-note">
        Sandbox subscriptions — recurring revenue shown here is excluded from live financial KPIs.
      </div>
    </div>
  );
}

function MoneyTab() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/financial/analytics").then(({ data }) => setF(data)).catch(() => {}); }, []);
  if (!f) return <Loading label="Loading money" />;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard label="Collected (live)" value={`$${f.total_collected.toLocaleString()}`} accent testid="money-collected" />
      <StatCard label="Effective Discount" value={`${f.effective_discount_pct}%`} testid="money-disc" />
      <StatCard label="Active Memberships" value={f.membership_active} testid="money-mem" />
      <StatCard label="Membership MRR" value={`$${f.membership_monthly_revenue}`} testid="money-mrr" />
    </div>
  );
}
